/**
 * Compile options on the project wiki page: in the user's own agent (free) or on AgentWiki (credits).
 * The `compile_mode` preference decides which one is shown first; the other stays one click away.
 */

import { Link } from 'react-router-dom'
import { useCompileMode } from '../../hooks/use-compile-mode'
import { agentCompileCommands } from '../../lib/agent-connect'
import { useT } from '../../i18n'
import { CopySnippet } from '../setup/copy-snippet'
import { badgeClass, hintClass } from '../ui/styles'
import { ServerCompilePanel } from './server-compile-panel'

export function AgentCompilePanel({ project }: { project: string }) {
  const t = useT()
  return (
    <section aria-labelledby="agent-compile-title" className="space-y-3 rounded-xl border border-border bg-surface-raised p-4 md:p-5">
      <div>
        <h2 id="agent-compile-title" className="flex flex-wrap items-center gap-2 font-display text-lg font-semibold text-fg">
          {t('compile.agent.title')}
          <span className={badgeClass}>{t('compile.agent.free')}</span>
        </h2>
        <p className="max-w-2xl text-sm text-fg-muted">{t('compile.agent.hint')}</p>
      </div>
      <CopySnippet code={agentCompileCommands(project)} label={t('compile.agent.commands')} />
      <p className={hintClass}>{t('compile.agent.mcp')}</p>
    </section>
  )
}

export function CompilePanel({ project }: { project: string }) {
  const t = useT()
  const mode = useCompileMode()
  const agent = <AgentCompilePanel project={project} />
  const server = <ServerCompilePanel project={project} />

  return (
    <div className="space-y-3" data-compile-mode={mode}>
      {mode === 'server' ? server : agent}
      <details className="group rounded-xl border border-border">
        <summary className="flex min-h-11 cursor-pointer items-center px-4 text-sm font-medium text-fg-muted hover:text-fg">
          {mode === 'server' ? t('compile.other.agent') : t('compile.other.server')}
        </summary>
        <div className="p-2 pt-0">{mode === 'server' ? agent : server}</div>
      </details>
      <p className={hintClass}>
        <Link to="/settings/preferences" className="hover:text-fg hover:underline">{t('compile.modeHint')}</Link>
      </p>
    </div>
  )
}
